import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import WebContactForm from '../components/WebContactForm';
import HandvaerkerfradragBox from '../components/HandvaerkerfradragBox';
import { ImageLightboxModal, ClickableWorkImage } from '../components/GalleryLightbox';
import { SITE_INFO, BRAND_ASSETS } from '../data';

export default function KloakservicePage() {
  const { pathname } = useLocation();
  const page = pathname.replace(/^\//, '') || 'kloak-service';

  const subpages = [
    { slug: 'kloak-service', label: 'Kloak & klimasikring', icon: 'fa-faucet-drip' },
    { slug: 'klimatilpasning', label: 'Klimasikring & skybrud', icon: 'fa-cloud-rain' },
    { slug: 'rottespaerre', label: 'Rottespærre', icon: 'fa-shield-halved' },
    { slug: 'minirensningsanlaeg', label: 'Minirenseanlæg', icon: 'fa-water' },
    { slug: 'tv-inspektion', label: 'TV-inspektion & spuling', icon: 'fa-video', isExternalRoute: true }
  ];

  const autKloakmester = SITE_INFO.contacts.find((c) => c.role.includes('kloak')) || {
    name: 'Lukas Corneliussen',
    phone: '51 93 21 41',
    email: 'Lukas@kts-entreprise.dk'
  };

  return (
    <div className="bg-stone-100 min-h-screen">
      <KloakSubTabs subpages={subpages} activeSlug={page} />

      {/* Render selected view */}
      {page === 'rottespaerre' && <RottespaerreView autKloakmester={autKloakmester} />}
      {(page === 'klimatilpasning' || page === 'skybrud') && <KlimasikringView autKloakmester={autKloakmester} />}
      {page === 'minirensningsanlaeg' && <MinirensningView autKloakmester={autKloakmester} />}
      {page === 'kloak-service' && <KloakOverview autKloakmester={autKloakmester} />}
    </div>
  );
}

function KloakSubTabs({ subpages, activeSlug }) {
  return (
    <div className="bg-stone-900 border-b border-stone-800 text-white shadow-xs sticky top-[57px] sm:top-[69px] lg:top-[85px] z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 overflow-x-auto py-2.5 no-scrollbar text-xs">
          <span className="text-stone-400 uppercase font-heading font-bold mr-2 whitespace-nowrap text-[11px]">
            Kloakmenu:
          </span>
          {subpages.map((sub) => {
            const active = activeSlug === sub.slug || (activeSlug === 'skybrud' && sub.slug === 'klimatilpasning');
            return (
              <Link
                key={sub.slug}
                to={`/${sub.slug}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xs font-heading font-bold uppercase tracking-wider whitespace-nowrap transition-colors ${
                  active
                    ? 'bg-[#0072c0] text-white shadow-xs'
                    : 'text-stone-300 hover:bg-stone-800 hover:text-white'
                }`}
              >
                <i className={`fa-solid ${sub.icon} text-[10px]`}></i>
                <span>{sub.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// 1. KLOAK OG KLIMASIKRING HOVEDSIDE
function KloakOverview({ autKloakmester }) {
  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 });

  const sewerImages = [
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kloak-maskine-broend-gul-hjelm.webp",
      alt: "Kloakarbejde med gravemaskine, brønd og medarbejder",
      caption: "Kloakarbejde med gravemaskine, brønd og medarbejder"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kloak-broende-etablering.webp",
      alt: "Brønde og rør under etablering",
      caption: "Brønde og rør under etablering"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kloak-faskine-gul-hjelm.webp",
      alt: "Medarbejder ved etablering af faskine",
      caption: "Medarbejder ved etablering af faskine"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kloak-faskine-roerfoering.webp",
      alt: "Faskine med tilsluttet rørføring",
      caption: "Faskine med tilsluttet rørføring"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kloak-ny-kloakinstallation.webp",
      alt: "Ny kloakinstallation under etablering",
      caption: "Ny kloakinstallation under etablering"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kloak-tank.webp",
      alt: "Tank under etablering i udgravning",
      caption: "Tank under etablering i udgravning"
    }
  ];
  const serviceBlocks = [
    {
      title: 'Klimasikring og skybrud',
      desc: 'Højvandslukke, pumpebrønde, faskiner, omfangsdræn og håndtering af regnvand.',
      slug: 'klimatilpasning',
      icon: 'fa-cloud-rain'
    },
    {
      title: 'TV-inspektion og kloakspuling',
      desc: 'Undersøgelse og rensning af kloakledninger samt lokalisering af brud, sætningsskader, rødder og rotteskader. Der kan udarbejdes rapport og dokumentation til blandt andet forsikringssager.',
      slug: 'tv-inspektion',
      icon: 'fa-video'
    },
    {
      title: 'Rottespærre',
      desc: 'Levering, autoriseret montering og efterfølgende service af godkendte rottespærrer.',
      slug: 'rottespaerre',
      icon: 'fa-shield-halved'
    },
    {
      title: 'Minirenseanlæg',
      desc: 'Levering og etablering af Watersystems WS Bioclean-minirenseanlæg.',
      slug: 'minirensningsanlaeg',
      icon: 'fa-water'
    },
    {
      title: 'Reparation og etablering af kloak',
      desc: 'Reparation, omlægning og etablering af kloakledninger og brønde for private og erhverv.',
      slug: 'kloak-service',
      icon: 'fa-wrench'
    }
  ];

  return (
    <>
      <PageSEO
        title="Kloak og klimasikring | Nordsjælland | KTS-Entreprise A/S"
        description="KTS-Entreprise er autoriseret kloakmester og udfører kloakarbejde, TV-inspektion, kloakspuling og løsninger til klimasikring for private, erhverv og offentlige bygherrer."
        ogTitle="Kloak og klimasikring | KTS-Entreprise A/S"
        ogDescription="Autoriseret kloakmester i Frederiksværk og Nordsjælland. Alt kloakarbejde, klimasikring, rottespærre, TV-inspektion og spuling."
        ogImage={BRAND_ASSETS.defaultOgImage}
        slug="kloak-service"
      />

      {/* Hero */}
      <section className="bg-stone-900 text-white pt-32 sm:pt-40 lg:pt-44 pb-12 sm:pb-16 border-b-4 border-[#0072c0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0072c0]/20 border border-[#0072c0]/40 text-blue-200 text-xs font-heading font-bold uppercase tracking-wider mb-3 rounded-xs">
            <span>Autoriseret kloakmester</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-white font-heading">
            Kloak og klimasikring
          </h1>
          <p className="text-stone-300 text-sm sm:text-base mt-2 max-w-2xl">
            KTS-Entreprise er autoriseret kloakmester og udfører kloakarbejde, TV-inspektion, kloakspuling og løsninger til klimasikring for private, erhverv og offentlige bygherrer i Nordsjælland og omegn.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        
        {/* Intro */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
          <div className="lg:col-span-7 space-y-6">
            <div className="border-l-4 border-[#0072c0] pl-4">
              <h2 className="text-2xl sm:text-3xl font-extrabold uppercase text-stone-900 tracking-tight font-heading">
                Autoriseret kloakarbejde og klimasikring
              </h2>
              <p className="text-stone-600 text-sm sm:text-base mt-1">
                Vi sikrer, at dit kloaksystem fungerer optimalt og overholder alle gældende lovkrav og standarder.
              </p>
            </div>

            <div className="space-y-4">
              {serviceBlocks.map((block) => (
                <div
                  key={block.title}
                  className="bg-white border border-stone-200 p-5 rounded-xs shadow-xs flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center gap-3 text-[#0072c0] mb-1.5">
                      <i className={`fa-solid ${block.icon} text-lg`}></i>
                      <h3 className="font-heading font-bold text-stone-900 text-base sm:text-lg uppercase">
                        {block.title}
                      </h3>
                    </div>
                    <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                      {block.desc}
                    </p>
                  </div>
                  {block.slug !== 'kloak-service' && (
                    <div className="mt-3 pt-2 border-t border-stone-100">
                      <Link
                        to={`/${block.slug}`}
                        className="inline-flex items-center gap-1.5 text-xs font-heading font-bold uppercase text-[#0072c0] hover:text-[#005b99]"
                      >
                        <span>Gå til {block.title}</span>
                        <i className="fa-solid fa-arrow-right text-[10px]"></i>
                      </Link>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Imagery & Contact */}
          <div className="lg:col-span-5 space-y-4">
            {/* Hovedbillede */}
            <div className="border border-stone-200 bg-white p-2 rounded-xs shadow-xs">
              <ClickableWorkImage
                src={sewerImages[0].src}
                alt={sewerImages[0].alt}
                className="w-full h-auto max-h-[380px] object-cover rounded-xs"
                style={{ objectPosition: 'center 40%' }}
                priority={true}
                onClick={() => setLightboxState({ isOpen: true, index: 0 })}
              />
            </div>

            <div className="bg-white border border-stone-200 p-6 rounded-xs shadow-xs">
              <div className="border-l-3 border-[#0072c0] pl-3 mb-4">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Faglig ansvarlig
                </span>
                <h3 className="font-heading font-bold text-lg text-stone-900 uppercase">
                  Kontakt vores autoriserede kloakmester
                </h3>
              </div>

              <div className="mb-4">
                <h4 className="font-heading font-bold text-stone-900 text-base">
                  {autKloakmester.name}
                </h4>
                <p className="text-xs text-stone-600 font-medium mt-0.5">
                  Autoriseret kloakmester
                </p>
              </div>

              <div className="space-y-2.5 pt-3 border-t border-stone-100 text-xs sm:text-sm">
                <a
                  href={`tel:${autKloakmester.phone.replace(/\s+/g, '')}`}
                  className="flex items-center gap-2.5 text-stone-800 hover:text-[#0072c0] font-mono font-bold transition-colors"
                >
                  <i className="fa-solid fa-phone text-[#0072c0]"></i>
                  <span>Tlf: {autKloakmester.phone}</span>
                </a>
                <a
                  href={`mailto:${autKloakmester.email}`}
                  className="flex items-center gap-2.5 text-stone-600 hover:text-[#0072c0] transition-colors"
                >
                  <i className="fa-solid fa-envelope text-[#0072c0]"></i>
                  <span>{autKloakmester.email}</span>
                </a>
              </div>
            </div>

            {/* Internal link to sugespidsanlæg */}
            <div className="bg-slate-50 border border-slate-200 p-5 rounded-xs space-y-2 text-xs">
              <span className="font-heading font-bold uppercase text-stone-900 block">
                Grundvandsproblemer ved udgravning?
              </span>
              <p className="text-stone-600">
                Vi råder over eget sugespidsanlæg til grundvandssænkning ved kloak- og anlægsarbejde.
              </p>
              <Link
                to="/entreprenerservice#sugespidsanlaeg"
                className="inline-flex items-center gap-1.5 font-heading font-bold uppercase text-[#0072c0] hover:text-[#005b99] pt-1"
              >
                <span>Læs om sugespidsanlæg</span>
                <i className="fa-solid fa-arrow-right text-[10px]"></i>
              </Link>
            </div>
          </div>
        </div>

        {/* Billeder fra vores kloakarbejde */}
        <section className="mb-16">
          <div className="border-l-4 border-[#0072c0] pl-4 mb-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold uppercase text-stone-900 tracking-tight font-heading">
              Billeder fra vores kloakarbejde
            </h2>
            <p className="text-stone-600 text-xs sm:text-sm mt-1">
              Eksempler på etablering af brønde, faskiner, nye rørledninger og tankanlæg i Nordsjælland.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Brønde under etablering */}
            <div className="bg-white border border-stone-200 p-3 rounded-xs shadow-xs flex flex-col justify-between">
              <div className="overflow-hidden rounded-xs bg-stone-100">
                <ClickableWorkImage
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/${sewerImages[1].src.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
                  alt={sewerImages[1].alt}
                  className="w-full h-52 object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 1 })}
                />
              </div>
              <div className="pt-3 px-1">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Kloakbrønde
                </span>
                <p className="text-xs font-bold text-stone-900 font-heading uppercase mt-0.5">
                  Brønde under etablering
                </p>
              </div>
            </div>

            {/* Etablering af faskine */}
            <div className="bg-white border border-stone-200 p-3 rounded-xs shadow-xs flex flex-col justify-between">
              <div className="overflow-hidden rounded-xs bg-stone-100">
                <ClickableWorkImage
                  src={sewerImages[2].src}
                  alt={sewerImages[2].alt}
                  className="w-full h-52 object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 2 })}
                />
              </div>
              <div className="pt-3 px-1">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Faskine
                </span>
                <p className="text-xs font-bold text-stone-900 font-heading uppercase mt-0.5">
                  Etablering af faskine i udgravning
                </p>
              </div>
            </div>

            {/* Faskine med rørføring */}
            <div className="bg-white border border-stone-200 p-3 rounded-xs shadow-xs flex flex-col justify-between">
              <div className="overflow-hidden rounded-xs bg-stone-100">
                <ClickableWorkImage
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/${sewerImages[3].src.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
                  alt={sewerImages[3].alt}
                  className="w-full h-52 object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 3 })}
                />
              </div>
              <div className="pt-3 px-1">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Regnvand
                </span>
                <p className="text-xs font-bold text-stone-900 font-heading uppercase mt-0.5">
                  Faskine med tilsluttet rørføring
                </p>
              </div>
            </div>

            {/* Ny kloakinstallation */}
            <div className="bg-white border border-stone-200 p-3 rounded-xs shadow-xs flex flex-col justify-between sm:col-span-2 lg:col-span-2">
              <div className="overflow-hidden rounded-xs bg-stone-100">
                <ClickableWorkImage
                  src={sewerImages[4].src}
                  alt={sewerImages[4].alt}
                  className="w-full h-56 object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 4 })}
                />
              </div>
              <div className="pt-3 px-1">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Røranlæg
                </span>
                <p className="text-xs font-bold text-stone-900 font-heading uppercase mt-0.5">
                  Ny kloakinstallation med rørføring i stabilgrus
                </p>
              </div>
            </div>

            {/* Tank under etablering */}
            <div className="bg-white border border-stone-200 p-3 rounded-xs shadow-xs flex flex-col justify-between">
              <div className="overflow-hidden rounded-xs bg-stone-100">
                <ClickableWorkImage
                  src={sewerImages[5].src}
                  alt={sewerImages[5].alt}
                  className="w-full h-56 object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 5 })}
                />
              </div>
              <div className="pt-3 px-1">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Tankanlæg
                </span>
                <p className="text-xs font-bold text-stone-900 font-heading uppercase mt-0.5">
                  Tank under etablering i udgravning
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 25. Håndværkerfradrag */}
        <HandvaerkerfradragBox />

        {/* Formular */}
        <div className="mt-16">
          <WebContactForm
            title="Kontakt vores autoriserede kloakmester"
            subtitle="Udfyld formularen for et uforpligtende tilbud på kloakarbejde eller klimasikring."
            submitText="Send forespørgsel"
            showAddress={true}
            showTaskTypeSelect={true}
            taskTypeOptions={[
              "Klimasikring / Skybrud",
              "Rottespærre montering/service",
              "Minirenseanlæg",
              "Kloakrenovering / Nyetablering",
              "Andet kloakarbejde"
            ]}
            showFileUpload={true}
            fileLabel="Vedhæft gerne billeder eller tegninger af kloakken (valgfrit)"
          />
        </div>

        {/* Lightbox for Kloak & Klimasikring */}
        <ImageLightboxModal
          isOpen={lightboxState.isOpen}
          onClose={() => setLightboxState({ ...lightboxState, isOpen: false })}
          images={sewerImages}
          currentIndex={lightboxState.index}
          onNavigate={(newIdx) => setLightboxState({ ...lightboxState, index: newIdx })}
          title="Kloak og klimasikring"
        />

      </div>
    </>
  );
}

// 2. SAMLET KLIMASIKRINGSSIDE (Afsnit 19)
function KlimasikringView({ autKloakmester }) {
  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 });

  const faskineImages = [
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kloak-faskine-gul-hjelm.webp",
      alt: "Medarbejder ved etablering af faskine",
      caption: "Medarbejder ved etablering af faskine"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kloak-faskine-roerfoering.webp",
      alt: "Faskine med tilsluttet rørføring",
      caption: "Faskine med tilsluttet rørføring"
    }
  ];

  return (
    <>
      <PageSEO
        title="Klimasikring og skybrud | Nordsjælland | KTS-Entreprise A/S"
        description="Højvandslukke, pumpebrønde, faskiner og omfangsdræn. KTS-Entreprise er autoriseret kloakmester og rådgiver om effektive løsninger mod oversvømmelse og regnvand."
        ogTitle="Klimasikring og skybrud | KTS-Entreprise A/S"
        ogDescription="Højvandslukke, pumpebrønde, faskiner og omfangsdræn udført af autoriseret kloakmester."
        ogImage={BRAND_ASSETS.defaultOgImage}
        slug="klimatilpasning"
      />

      <section className="bg-stone-900 text-white pt-32 sm:pt-40 lg:pt-44 pb-12 sm:pb-16 border-b-4 border-[#0072c0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0072c0]/20 border border-[#0072c0]/40 text-blue-200 text-xs font-heading font-bold uppercase tracking-wider mb-3 rounded-xs">
            <span>Klimasikring</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-white font-heading">
            Klimasikring og skybrud
          </h1>
          <p className="text-stone-300 text-sm sm:text-base mt-2 max-w-2xl">
            Løsninger til forebyggelse af oversvømmelser, tilbageløb og håndtering af store mængder regnvand for private og erhverv.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
          <div className="lg:col-span-8 space-y-6">
            
            {/* Højvandslukke */}
            <div className="bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs space-y-2">
              <div className="border-l-4 border-[#0072c0] pl-3 mb-2">
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Højvandslukke
                </h2>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Et højvandslukke kan forhindre kloakvand i at løbe baglæns gennem afløb og toiletter, når kloaksystemet er overbelastet. Løsningen skal vælges og placeres ud fra ejendommens afløbsforhold og anvendelse.
              </p>
              <p className="text-stone-700 text-xs sm:text-sm font-semibold text-[#0072c0] pt-1">
                Arbejdet udføres af vores autoriserede kloakmestervirksomhed i henhold til gældende standarder.
              </p>
            </div>

            {/* Pumpebrønd */}
            <div className="bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs space-y-2">
              <div className="border-l-4 border-[#0072c0] pl-3 mb-2">
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Pumpebrønd
                </h2>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                En pumpebrønd kan anvendes, når spildevand eller regnvand ikke kan ledes væk ved almindeligt fald. Pumpen fører vandet videre til det relevante afløbssystem.
              </p>
            </div>

            {/* Omfangsdræn og fugtsikring */}
            <div className="bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs space-y-2">
              <div className="border-l-4 border-[#0072c0] pl-3 mb-2">
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Omfangsdræn og fugtsikring
                </h2>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Et omfangsdræn kan lede overskydende vand væk fra fundament og kældervægge. Behovet bør vurderes ud fra bygningens konstruktion, jordbundsforhold og årsagen til fugtproblemet.
              </p>
            </div>

            {/* Faskiner og håndtering af regnvand */}
            <div className="bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs space-y-2">
              <div className="border-l-4 border-[#0072c0] pl-3 mb-2">
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Faskiner og håndtering af regnvand
                </h2>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                En faskine opsamler regnvand midlertidigt og lader det sive ned i jorden. Størrelse og placering afhænger blandt andet af tagareal, jordbund, grundvandsforhold og afstand til bygninger og skel. Etablering kræver kommunens tilladelse.
              </p>
            </div>

            {/* En løsning til den enkelte ejendom */}
            <div className="bg-slate-50 p-6 sm:p-8 border border-slate-200 rounded-xs space-y-2">
              <h3 className="font-heading font-bold uppercase text-stone-900 text-lg">
                En løsning til den enkelte ejendom
              </h3>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Klimasikring kræver ofte en kombination af flere løsninger. Vi gennemgår kloakforhold, terræn og bygningens placering, inden vi foreslår det videre forløb.
              </p>
            </div>

          </div>

          {/* Right Column: Contact Card */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white border border-stone-200 p-6 rounded-xs shadow-xs">
              <div className="border-l-3 border-[#0072c0] pl-3 mb-4">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Faglig rådgivning
                </span>
                <h3 className="font-heading font-bold text-lg text-stone-900 uppercase">
                  Kontakt vores autoriserede kloakmester
                </h3>
              </div>

              <div className="mb-4">
                <h4 className="font-heading font-bold text-stone-900 text-base">
                  Lukas Corneliussen
                </h4>
                <p className="text-xs text-stone-600 font-medium mt-0.5">
                  Autoriseret kloakmester
                </p>
              </div>

              <div className="space-y-2.5 pt-3 border-t border-stone-100 text-xs sm:text-sm">
                <a
                  href="tel:51932141"
                  className="flex items-center gap-2.5 text-stone-800 hover:text-[#0072c0] font-mono font-bold transition-colors"
                >
                  <i className="fa-solid fa-phone text-[#0072c0]"></i>
                  <span>Telefon: 51 93 21 41</span>
                </a>
                <a
                  href="mailto:Lukas@kts-entreprise.dk"
                  className="flex items-center gap-2.5 text-stone-600 hover:text-[#0072c0] transition-colors"
                >
                  <i className="fa-solid fa-envelope text-[#0072c0]"></i>
                  <span>Lukas@kts-entreprise.dk</span>
                </a>
              </div>
            </div>

            <div className="border border-stone-200 bg-white p-2 rounded-xs shadow-xs">
              <ClickableWorkImage
                src={faskineImages[0].src}
                alt={faskineImages[0].alt}
                className="w-full h-auto object-cover rounded-xs"
                onClick={() => setLightboxState({ isOpen: true, index: 0 })}
              />
            </div>

            <div className="border border-stone-200 bg-white p-2 rounded-xs shadow-xs">
              <ClickableWorkImage
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/${faskineImages[1].src.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
                alt={faskineImages[1].alt}
                className="w-full h-auto object-cover rounded-xs"
                onClick={() => setLightboxState({ isOpen: true, index: 1 })}
              />
            </div>
          </div>
        </div>

        {/* 25. Håndværkerfradrag */}
        <HandvaerkerfradragBox />

        {/* Formular (Afsnit 19) */}
        <div className="mt-16">
          <WebContactForm
            title="Få hjælp til klimasikring"
            subtitle="Udfyld formularen med dine oplysninger, så gennemgår vi forholdene sammen med dig."
            submitText="Få hjælp til klimasikring"
            showAddress={true}
            showTaskTypeSelect={true}
            taskTypeOptions={[
              "Hvor opleves problemet: Kælder",
              "Hvor opleves problemet: Terræn / have",
              "Hvor opleves problemet: Kloak / tilbageløb",
              "Hvor opleves problemet: Fundament / sokkel",
              "Andet problem"
            ]}
            customFields={
              <div>
                <label htmlFor="previous_flood" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                  Har der tidligere været oversvømmelse?
                </label>
                <select
                  id="previous_flood"
                  name="previous_flood"
                  className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
                >
                  <option value="Nej">Nej, forebyggende henvendelse</option>
                  <option value="Ja, inden for det seneste år">Ja, inden for det seneste år</option>
                  <option value="Ja, gentagne gange">Ja, gentagne gange ved kraftig regn</option>
                  <option value="Ved ikke / nyindflyttet">Ved ikke / nyindflyttet</option>
                </select>
              </div>
            }
            showFileUpload={true}
            fileLabel="Vedhæft gerne billeder af det udsatte område (valgfrit)"
          />
        </div>

        {/* Lightbox for Klimasikring */}
        <ImageLightboxModal
          isOpen={lightboxState.isOpen}
          onClose={() => setLightboxState({ ...lightboxState, isOpen: false })}
          images={faskineImages}
          currentIndex={lightboxState.index}
          onNavigate={(newIdx) => setLightboxState({ ...lightboxState, index: newIdx })}
          title="Klimasikring og skybrud"
        />

      </div>
    </>
  );
}

// 3. ROTTESPÆRRE SIDE (Afsnit 18)
function RottespaerreView({ autKloakmester }) {
  return (
    <>
      <PageSEO
        title="Rottespærre – montering og service | KTS-Entreprise A/S"
        description="KTS-Entreprise er autoriseret kloakmestervirksomhed og certificeret rottespærremontør. Vi monterer og servicerer VA-godkendte rottespærrer i Halsnæs og Nordsjælland."
        ogTitle="Rottespærre – montering og service | KTS-Entreprise A/S"
        ogDescription="Certificeret montering og service af VA-godkendte rottespærrer i Nordsjælland."
        ogImage={BRAND_ASSETS.defaultOgImage}
        slug="rottespaerre"
      />

      <section className="bg-stone-900 text-white pt-32 sm:pt-40 lg:pt-44 pb-12 sm:pb-16 border-b-4 border-[#0072c0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0072c0]/20 border border-[#0072c0]/40 text-blue-200 text-xs font-heading font-bold uppercase tracking-wider mb-3 rounded-xs">
            <span>Certificeret rottespærremontør</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-white font-heading">
            Rottespærre – montering og service
          </h1>
          <p className="text-stone-300 text-sm sm:text-base mt-2 max-w-2xl">
            KTS-Entreprise er autoriseret kloakmestervirksomhed og certificeret rottespærremontør. Vi monterer og servicerer VA-godkendte rottespærrer i Halsnæs og resten af Nordsjælland.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-14">
          <div className="lg:col-span-7 space-y-4">
            
            {/* Hvad er en rottespærre? */}
            <div className="bg-white p-5 sm:p-6 border border-stone-200 rounded-xs shadow-xs space-y-2.5">
              <div className="border-l-4 border-[#0072c0] pl-3 mb-1">
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Hvad er en rottespærre?
                </h2>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                En rottespærre monteres i en egnet kloakbrønd og forhindrer rotter i at bevæge sig fra hovedkloakken og ind i ejendommens kloaksystem. Rottespærrens klapper tillader spildevandet at passere ud, men spærrer for rotternes vej ind.
              </p>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed font-medium">
                Vi monterer VA-godkendte rottespærrer, der passer til den konkrete kloakledning og brønd. Inden montering vurderer vi adgangsforholdene og kloaksystemets tilstand.
              </p>
            </div>

            {/* Certificeret montering */}
            <div className="bg-white p-5 sm:p-6 border border-stone-200 rounded-xs shadow-xs space-y-2.5">
              <div className="border-l-4 border-[#0072c0] pl-3 mb-1">
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Certificeret montering
                </h2>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Montering af en rottespærre skal udføres korrekt, så den ikke giver problemer med kloakkens funktion. Hos KTS-Entreprise udføres arbejdet af en certificeret rottespærremontør under virksomhedens kloakautorisation.
              </p>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Miljøstyrelsens regler kræver et særligt kursus og en bestået prøve for at opnå certifikat som rottespærremontør. Vi er certificeret rottespærremontør og følger den gældende rottebekendtgørelse.
              </p>
              <div className="pt-1">
                <a
                  href="https://mst.dk/erhverv/rent-miljoe-og-cirkulaer-oekonomi/kemikalier/bekaempelsesmidler/rottebekaempelse"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-heading font-bold uppercase text-[#0072c0] hover:text-[#005b99]"
                >
                  <span>Læs om rottebekæmpelse hos Miljøstyrelsen</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-[10px]"></i>
                </a>
              </div>
            </div>

            {/* Service og vedligeholdelse */}
            <div className="bg-white p-5 sm:p-6 border border-stone-200 rounded-xs shadow-xs space-y-2.5">
              <div className="border-l-4 border-[#0072c0] pl-3 mb-1">
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Service og vedligeholdelse
                </h2>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                En rottespærre bør kontrolleres og rengøres regelmæssigt efter producentens anvisninger. Vi tilbyder service og funktionskontrol, så spærren fortsat kan fungere korrekt.
              </p>
            </div>

          </div>

          {/* Right Column: Contact Person & Info */}
          <div className="lg:col-span-5 space-y-4">
            <div className="bg-white border border-stone-200 p-6 rounded-xs shadow-xs">
              <div className="border-l-3 border-[#0072c0] pl-3 mb-4">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Certificeret montør
                </span>
                <h3 className="font-heading font-bold text-lg text-stone-900 uppercase">
                  Kontaktperson
                </h3>
              </div>

              <div className="mb-4">
                <h4 className="font-heading font-bold text-stone-900 text-base">
                  Kim Hougaard Jensen
                </h4>
              </div>

              <div className="space-y-2.5 pt-3 border-t border-stone-100 text-xs sm:text-sm">
                <a
                  href="tel:23652577"
                  className="flex items-center gap-2.5 text-stone-800 hover:text-[#0072c0] font-mono font-bold transition-colors"
                >
                  <i className="fa-solid fa-phone text-[#0072c0]"></i>
                  <span>Telefon: 23 65 25 77</span>
                </a>
                <a
                  href="mailto:Kim@kts-entreprise.dk"
                  className="flex items-center gap-2.5 text-stone-600 hover:text-[#0072c0] transition-colors"
                >
                  <i className="fa-solid fa-envelope text-[#0072c0]"></i>
                  <span>Kim@kts-entreprise.dk</span>
                </a>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 p-5 rounded-xs space-y-2.5 text-xs sm:text-sm text-stone-700">
              <span className="font-heading font-bold uppercase text-stone-900 block text-xs tracking-wider">
                Hvorfor montere en rottespærre?
              </span>
              <ul className="space-y-2 text-xs text-stone-600">
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-shield-halved text-[#0072c0] mt-0.5 shrink-0"></i>
                  <span>Effektiv mekanisk barriere, der holder rotter ude af afløb og faldstammer</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-certificate text-[#0072c0] mt-0.5 shrink-0"></i>
                  <span>VA-godkendt kvalitet monteret efter gældende rottebekendtgørelse</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-clipboard-check text-[#0072c0] mt-0.5 shrink-0"></i>
                  <span>Mulighed for årlig serviceaftale og eftersyn</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* 3D Demonstration af rottespærre (Vimeo) - fremhævet fuldbredde sektion */}
        <section className="bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs mb-14">
          <div className="max-w-3xl mb-6">
            <div className="border-l-4 border-[#0072c0] pl-3 mb-1">
              <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                Sådan fungerer en 3D-rottespærre
              </h2>
            </div>
            <p className="text-stone-600 text-xs sm:text-sm mt-1">
              Se en visuel demonstration af rottespærrens funktion og montering i kloakbrønden.
            </p>
          </div>
          
          <div className="relative aspect-16/9 w-full max-w-4xl mx-auto overflow-hidden rounded-xs bg-stone-950 border border-stone-200 shadow-sm">
            <iframe
              src="https://player.vimeo.com/video/1109919728?h=0a9398244b"
              title="Nordisk Innovation 3D Rottespærre Villa DK"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </section>

        {/* Formular (Afsnit 18) */}
        <div className="mt-16">
          <WebContactForm
            title="Send forespørgsel om rottespærre"
            subtitle="Udfyld formularen for montering, service eller rådgivning om rottespærrer."
            submitText="Send forespørgsel om rottespærre"
            showAddress={true}
            showTaskTypeSelect={true}
            taskTypeOptions={[
              "Ny montering af rottespærre",
              "Service og funktionskontrol af eksisterende rottespærre",
              "Spørgsmål eller rådgivning om rotter i kloakken"
            ]}
            customFields={
              <div>
                <label htmlFor="well_info" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                  Oplysninger om eksisterende brønd (hvis kendt)
                </label>
                <input
                  id="well_info"
                  name="well_info"
                  type="text"
                  placeholder="F.eks. plastbrønd, betonbrønd, rørdimension Ø110/Ø160"
                  className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
                />
              </div>
            }
            showFileUpload={true}
            fileLabel="Vedhæft gerne billeder af brønden (valgfrit)"
          />
        </div>

      </div>
    </>
  );
}

// 4. MINIRENSEANLÆG SIDE (Afsnit 21)
function MinirensningView({ autKloakmester }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const minirensImage = {
    src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/minirenseanlaeg-arbejdsfoto.webp",
    alt: "Etablering af minirenseanlæg på grund",
    caption: "Etablering af minirenseanlæg på grund"
  };

  return (
    <>
      <PageSEO
        title="Minirenseanlæg til ejendomme uden offentlig kloak | KTS-Entreprise A/S"
        description="KTS-Entreprise etablerer, udskifter og servicerer minirenseanlæg ved ejendomme, som ikke er tilsluttet den offentlige kloak. Autoriseret kloakmester i Nordsjælland."
        ogTitle="Minirenseanlæg | KTS-Entreprise A/S"
        ogDescription="Levering og etablering af Watersystems WS Bioclean-minirenseanlæg til ejendomme i det åbne land."
        ogImage={BRAND_ASSETS.defaultOgImage}
        slug="minirensningsanlaeg"
      />

      <section className="bg-stone-900 text-white pt-32 sm:pt-40 lg:pt-44 pb-12 sm:pb-16 border-b-4 border-[#0072c0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0072c0]/20 border border-[#0072c0]/40 text-blue-200 text-xs font-heading font-bold uppercase tracking-wider mb-3 rounded-xs">
            <span>Ejendomme i det åbne land</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-white font-heading">
            Minirenseanlæg til ejendomme uden offentlig kloak
          </h1>
          <p className="text-stone-300 text-sm sm:text-base mt-2 max-w-2xl">
            KTS-Entreprise etablerer, udskifter og servicerer minirenseanlæg ved ejendomme, som ikke er tilsluttet den offentlige kloak.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
          <div className="lg:col-span-8 space-y-6">
            
            <div className="bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs space-y-4">
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Vi hjælper med at finde en løsning, der passer til ejendommen og de krav, der fremgår af kommunens tilladelse. Arbejdet udføres af vores autoriserede kloakmester.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs space-y-3">
              <div className="border-l-4 border-[#0072c0] pl-3 mb-2">
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Vi kan blandt andet hjælpe med:
                </h2>
              </div>
              <ul className="space-y-2 text-stone-700 text-sm sm:text-base list-disc pl-4">
                <li>Etablering og udskiftning af minirenseanlæg</li>
                <li>Jord- og kloakarbejde i forbindelse med anlægget</li>
                <li>Tilslutning og færdiggørelse</li>
                <li>Service og vedligeholdelse</li>
                <li>Vejledning om den praktiske proces</li>
              </ul>
              <div className="p-3.5 bg-amber-50 border border-amber-200 text-stone-800 text-xs sm:text-sm mt-4 rounded-xs">
                <strong>Vigtigt:</strong> Etablering eller udskiftning af et minirenseanlæg kræver altid <strong>kommunal tilladelse (nedsivningstilladelse eller udledningstilladelse)</strong>, før arbejdet kan påbegyndes.
              </div>
            </div>

          </div>

          {/* Right Column: Contact Person */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white border border-stone-200 p-6 rounded-xs shadow-xs">
              <div className="border-l-3 border-[#0072c0] pl-3 mb-4">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Autoriseret kloakmester
                </span>
                <h3 className="font-heading font-bold text-lg text-stone-900 uppercase">
                  Kontaktperson
                </h3>
              </div>

              <div className="mb-4">
                <h4 className="font-heading font-bold text-stone-900 text-base">
                  Lukas Corneliussen
                </h4>
                <p className="text-xs text-stone-600 font-medium mt-0.5">
                  Autoriseret kloakmester
                </p>
              </div>

              <div className="space-y-2.5 pt-3 border-t border-stone-100 text-xs sm:text-sm">
                <a
                  href="tel:51932141"
                  className="flex items-center gap-2.5 text-stone-800 hover:text-[#0072c0] font-mono font-bold transition-colors"
                >
                  <i className="fa-solid fa-phone text-[#0072c0]"></i>
                  <span>Telefon: 51 93 21 41</span>
                </a>
                <a
                  href="mailto:Lukas@kts-entreprise.dk"
                  className="flex items-center gap-2.5 text-stone-600 hover:text-[#0072c0] transition-colors"
                >
                  <i className="fa-solid fa-envelope text-[#0072c0]"></i>
                  <span>Lukas@kts-entreprise.dk</span>
                </a>
              </div>
            </div>

            <div className="bg-white border border-stone-200 p-2 rounded-xs">
              <ClickableWorkImage
                src={minirensImage.src}
                alt={minirensImage.alt}
                className="w-full h-auto max-h-[480px] object-contain mx-auto rounded-xs"
                onClick={() => setLightboxOpen(true)}
              />
            </div>
          </div>
        </div>

        {/* 25. Håndværkerfradrag */}
        <HandvaerkerfradragBox />

        {/* Formular (Afsnit 21) */}
        <div className="mt-16">
          <WebContactForm
            title="Forespørgsel på minirenseanlæg"
            subtitle="Udfyld formularen for rådgivning eller tilbud på etablering/udskiftning af minirenseanlæg."
            submitText="Send forespørgsel om minirenseanlæg"
            showAddress={true}
            customFields={
              <div className="space-y-4">
                <div>
                  <label htmlFor="existing_drainage" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                    Eksisterende kloakforhold
                  </label>
                  <select
                    id="existing_drainage"
                    name="existing_drainage"
                    className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
                  >
                    <option value="Septiktank / bundfældningstank">Septiktank / bundfældningstank</option>
                    <option value="Gammelt minirenseanlæg">Gammelt minirenseanlæg, der skal udskiftes</option>
                    <option value="Samletank">Samletank</option>
                    <option value="Nybyggeri uden eksisterende kloak">Nybyggeri uden eksisterende kloak</option>
                    <option value="Andet / ved ikke">Andet / ved ikke</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="municipality_permit" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                    Har kommunen allerede givet en tilladelse?
                  </label>
                  <select
                    id="municipality_permit"
                    name="municipality_permit"
                    className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
                  >
                    <option value="Nej, skal søges">Nej, skal søges</option>
                    <option value="Ja, tilladelse er modtaget">Ja, tilladelse er modtaget</option>
                    <option value="Ansøgning er indsendt til kommunen">Ansøgning er indsendt til kommunen</option>
                  </select>
                </div>
              </div>
            }
            showFileUpload={true}
            fileLabel="Vedhæft gerne situationsplan eller kommunal tilladelse (valgfrit)"
          />
        </div>

        {/* Lightbox for Minirenseanlæg */}
        <ImageLightboxModal
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          images={[minirensImage]}
          currentIndex={0}
          title="Minirenseanlæg"
        />

      </div>
    </>
  );
}
